import React, { useState, useEffect, useContext} from 'react';
import '../view-archer-styles.css';

// material-ui
import { Box, Button, CircularProgress,FormControl, Grid, Stack} from '@mui/material/';

//components import
import DashboardPendingResponses from './DashboardPendingResponses';
import DashboardDraftLeters from './DashboardDraftLetters';
import AuditorsDropdown from '../../../components/reference/AuditorsDropdown';
import DashboardEmailLogs from './DashboardEmailLogs';
import DashboardUpcoming205s from './DashboardUpcoming205s';
import DashboardIssuesChart from './DashboardIssuesChart';
import DashboardPendingReferrals from './DashboardPendingReferrals';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';

import { AuditorCommunicationContext } from '../view-archer';
import ArcherLetter from '../dataObjects/ArcherLetter';
import FilerResponse from '../dataObjects/FilerResponse';

//const data
import AuditorList from '../../../data/AuditorList';
import EmailLog from '../../../dataObjects/EmailLog';
import LegalReferral from '../dataObjects/LegalReferral';
import ArcherIssueTemplateEditor from './ArcherIssueTemplateEditor';
import ArcherIssue from '../dataObjects/ArcherIssue';
import DashboardDelinquentIssues from './DashboardDelinquentIssues';

//const data
const AUDITORS = AuditorList;

interface Props {
    displayStatus: string;
    toggleDisplayStatus: (display: string)=> void
}

const Dashboard = (props: Props) => {

    const context = useContext(AuditorCommunicationContext);

    /*initial state set up*/
    const [draftLetters, setDraftLetters] = useState<ArcherLetter[]>();
    const [pendingResponses, setPendingResponses] = useState<FilerResponse[]>();
    const [upcomingFollowupLetters, setUpcomingFollowupLetters] = useState<ArcherLetter[]>();
    const [delinquentFollowupIssues, setDelinquentFollowupIssues] = useState<ArcherIssue[]>();
    const [failedEmailLogs, setFailedEmailLogs] = useState<EmailLog[]>();
    const [pendingReferrals, setPendingReferrals] = useState<LegalReferral[]>();
    const [chartNames, setChartNames] = useState<string[]>();
    const [chartValues, setChartValues] = useState<number[]>();
    const [isIssueTemplateEditorOpen, setIssueTemplateEditorOpen] = useState(false);

    //loading indicator
    const [isLoading, setLoadingIndicator] = useState('');

    useEffect(() => {
        //initial load
        if (props.displayStatus === "active" ) {
            fetchDashboardData();
        }
    }, [])

    const fetchDashboardData = async() => {
        await fetch(`/archer3/GetDashboardData?auditorId=${context.currentAuditorId}`)
        .then(result => result.json())
        .then(result => {
            if (result.isError) {
                let info = {
                    type: 'error',
                    title: 'Error: GetDashboardData',
                    errorMessage: result.errorMessage,
                    secondaryErrorMessage: result.secondaryErrorMessage
                };
                setAlertInfo(info);
                setAlertInfoDialog(true);
            }
            else {
                //get current auditor id from dashboard data?
                let data = result.result;
                setDraftLetters(data.draftLetters);
                setPendingResponses(data.pendingResponses);
                setUpcomingFollowupLetters(data.upcomingFollowupLetters);
                setDelinquentFollowupIssues(data.delinquentFollowupIssues);
                setFailedEmailLogs(data.failedEmailDeliveries);
                setPendingReferrals(data.pendingReferrals);

                //prepare issues chart data
                setChartNames(Object.values(data.issueStatusCounts).map((item: any) => item.issueStatusDescription));
                setChartValues(Object.values(data.issueStatusCounts).map((item: any) => item.issueCount));

                setLoadingIndicator('hidden');

                //set the current auditor: no need
                //updateAuditorId(data.auditorId);
                //parseUrl();
            }
        }
       )
    }

    const markLetterRecipientAsReviewed = (letterRecipientId: number) => {

        let url = `/Archer3/MarkLetterRecipientAsReviewed?letterRecipientId=${letterRecipientId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveLetterSubject',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    fetchDashboardData();
                }
            })
    }

    const updateAuditorId = (auditorId: number) => {
        context.setCurrentAuditorId(auditorId);
    }

    const editReferral = () => {
        props.toggleDisplayStatus("referrals");
    }

    /*Issue Template Editor*/
    const openIssueTemplateEditor = () => {
        setIssueTemplateEditorOpen(true);
    }

    const closeIssueTemplateEditor = () => {
        setIssueTemplateEditorOpen(false);
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div className={`dashboard ${props.displayStatus}`}>
            <Box sx={{ flexGrow: 1}}>
                <Grid container spacing={2} sx={{marginBottom:2}} >
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <FormControl sx={{ marginTop: "10px !important", maxWidth: 150 }} size="small">
                                <Button size="small" className="small-action-button" variant="outlined" onClick={openIssueTemplateEditor}>Issue Editor</Button>
                            </FormControl>
                            <AuditorsDropdown auditorId={context.currentAuditorId} auditors={AUDITORS} updateAuditorId={updateAuditorId}></AuditorsDropdown>
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <DashboardIssuesChart chartNames={chartNames} chartValues={chartValues}></DashboardIssuesChart>
                    </Grid>
                    <Grid item xs={6}>
                        <DashboardDraftLeters draftLetters={draftLetters} ></DashboardDraftLeters>
                    </Grid>
                    <Grid item xs={6}>
                        <DashboardPendingResponses pendingResponses={pendingResponses} ></DashboardPendingResponses>
                    </Grid>
                    <Grid item xs={6}>
                        <DashboardDelinquentIssues delinquentIssues={delinquentFollowupIssues} ></DashboardDelinquentIssues>
                    </Grid>
                    <Grid item xs={6}>
                        <DashboardUpcoming205s upcomingFollowupLetters={upcomingFollowupLetters}></DashboardUpcoming205s>
                    </Grid>
                    <Grid item xs={6}>
                        <DashboardEmailLogs markLetterRecipientAsReviewed={markLetterRecipientAsReviewed} failedEmailLogs={failedEmailLogs} ></DashboardEmailLogs>
                    </Grid>
                    <Grid item xs={6}>
                        <DashboardPendingReferrals pendingReferrals={pendingReferrals} editReferral={editReferral}></DashboardPendingReferrals>
                    </Grid>
                </Grid>
            </Box>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
            {/*issue type template*/}
            <ArcherIssueTemplateEditor isIssueTemplateEditorOpen={isIssueTemplateEditorOpen} closeIssueTemplateEditor={closeIssueTemplateEditor}></ArcherIssueTemplateEditor>
            {/*loading indicator*/}
            <CircularProgress
                size={50}
                className={isLoading}
                sx={{
                    position: "fixed",
                    left: "50%",
                    top: "30%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2
                }}
            />
        </div>
   )
}

export default Dashboard;